import { useAuthStore } from "@/stores/auth.store";
import { newIdempotencyKey } from "@/services/posSaleEnvelope";
import { isOffline } from "@/services/offlineSignal";
import { posReturnsCall } from "@/api/posReturnsApi";
import { readDurablePosAttempt, persistDurablePosAttempt, releaseDurablePosAttempt, validatePosAttemptResult, type DurablePosAttempt } from "@/services/posReturnDurability";

export type PosReturnAttempt = DurablePosAttempt;

function storageKey(profile: string) {
  const auth = useAuthStore();
  const user = auth.username || auth.user?.username || auth.user?.email || auth.user?.name;
  if (!user || !profile)
    throw new Error("alkokh.posReturns.identityRequired");
  return `alkokh.pos-return.v1:${encodeURIComponent(user)}:${encodeURIComponent(profile)}`;
}

/** A storage error must refuse the write. Never treat unreadable state as no attempt. */
export function readPosReturnAttempt(profile: string): PosReturnAttempt | null {
  const key = storageKey(profile);
  try { return readDurablePosAttempt(localStorage, key); }
  catch (error) {
    console.warn("Could not read durable POS financial request", error);
    throw new Error("alkokh.posReturns.storageFailed");
  }
}

export function savePosReturnAttempt(profile: string, kind: PosReturnAttempt["kind"], body: Record<string, unknown>) {
  if (isOffline())
    throw new Error("alkokh.posReturns.onlineRequired");
  const key = storageKey(profile);
  try { return persistDurablePosAttempt(localStorage, key, kind, body, newIdempotencyKey()); }
  catch (error: any) {
    if (error?.message === "alkokh.posReturns.pending") throw error;
    console.warn("Could not persist durable POS financial request", error);
    throw new Error("alkokh.posReturns.storageFailed");
  }
}

/** Recovery always posts the frozen request, never a newly edited cart. No offline queue. */
export async function submitPosReturnAttempt<T>(profile: string, attempt: PosReturnAttempt): Promise<T> {
  if (isOffline())
    throw new Error("alkokh.posReturns.onlineRequired");
  const saved = readPosReturnAttempt(profile);
  if (!saved || saved.key !== attempt.key)
    throw new Error("alkokh.posReturns.storageFailed");
  const result = await posReturnsCall<{ status: "completed" | "refused"; result?: T; message?: string }>(
    attempt.kind === "return" ? "execute" : "create_credit_sale",
    { ...saved.body, idempotency_key: saved.key },
  );
  // Only this explicit, terminal protocol can release a key. HTTP errors are ambiguous.
  if (result.status === "refused") {
    releaseDurablePosAttempt(localStorage, storageKey(profile), saved.key);
    throw new Error(result.message || "alkokh.posReturns.refused");
  }
  if (result.status !== "completed" || !validatePosAttemptResult(attempt.kind, result.result))
    throw new Error("alkokh.posReturns.pending");
  // Removal failure leaves a harmless replay, never a new financial operation.
  releaseDurablePosAttempt(localStorage, storageKey(profile), saved.key);
  return result.result as T;
}
